import {
  ApiBlockSettings,
  ApiSettings,
  ApiSlideSettings,
  BlockSettings,
} from 'apps/arsultima/src/app/models/settings.model';
import { ArsultimaForm, FormTypes } from './../../../../models/forms.model';
import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { ContentType, UploadFieldName } from 'apps/arsultima/src/app/models/content.enum';
import { FormBuilder, Validators } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';
import { ArsultimaErrorTypes } from 'apps/arsultima/src/app/config/errors.config';
import { ContentService } from 'apps/arsultima/src/app/services/content.service';
import { DeleteModalComponent } from '../../../modal/components/delete-modal/delete-modal.component';
import { FORM_VALIDATORS } from 'apps/arsultima/src/app/config/forms-validation.config';
import { IMAGES_FILENAME_PREFIX } from 'apps/arsultima/src/app/models/image.model';
import { ImageService } from './../../../../services/image.service';
import { KeyboardKeys } from './../../../../models/keyboard.model';
import { MatDialog } from '@angular/material/dialog';
import { NavigateService } from './../../../../core/authentication/services/navigate/navigate.service';
import { SAVE_SUBMISSION_ERROR } from '../../../../config/errors.config';
import { SharedService } from './../../../../services/shared.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SnackbarService } from '../../../../core/services/snackbar.service';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'arsultima-block-edit',
  templateUrl: './block-edit.component.html',
  styleUrls: ['./block-edit.component.scss'],
})
export class BlockEditComponent implements OnInit, ArsultimaForm {
  @HostListener('document:keydown', ['$event'])
  handleArrowKeys(event: KeyboardEvent) {
    switch (event.key) {
      case KeyboardKeys.ESCAPE: {
        this.goBack();
        break;
      }
    }
  }

  @Output() dismiss = new EventEmitter();

  formType = FormTypes.BLOCK;
  type = ContentType.blocks;

  page: string;
  id: string;
  editMode = false;

  numberOfRotation = 0;

  form = this.fb.group({
    ID: [''],
    date_maj: [''],
    page: ['home'],
    rank: ['2', [Validators.required, ...FORM_VALIDATORS.rank]],
    blocktitle: ['', [Validators.required, ...FORM_VALIDATORS.longTitle]],
    blocksubtitle: ['', [...FORM_VALIDATORS.longTitle]],
    blocktext: ['', [...FORM_VALIDATORS.textArea]],
    nom_pic: [''],
  });
  uploadFieldName: UploadFieldName = UploadFieldName.pic;
  imageUrl: string;
  uploading = false;
  currentRank = 1;

  saveService: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly imageService: ImageService,
    private readonly contentService: ContentService,
    private readonly sharedService: SharedService,
    private readonly route: ActivatedRoute,
    private readonly navigateService: NavigateService,
    public dialog: MatDialog,
    private readonly snackbarService: SnackbarService,
  ) {
    this.saveService = this.contentService.saveBlock.bind(this.contentService);
    // this.form.valueChanges.subscribe(formState => {
    //   console.log('Form State:', formState);
    //   console.log(this.form.controls.errors);
    // });
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    this.page = this.route.snapshot.queryParams.page;
    this.currentRank = Number(this.route.snapshot.queryParams.currentRank);

    this.editMode = this.id != null;
    if (!this.editMode) {
      this.form.patchValue({
        rank: this.currentRank + 1,
      });
    }
    this.getSettings();
  }

  get fileName() {
    return this.form.get('nom_pic').value;
  }

  rotateImage(event: number): void {
    this.numberOfRotation = event;
  }

  upload(files: FileList) {
    this.uploading = true;
    const file = files.item(0);
    if (this.id) {
      this.sharedService.saveBeforeDoingOtherAction(this.form, this.saveService).subscribe(data => {
        if (data !== SAVE_SUBMISSION_ERROR) {
          this.contentService
            .uploadSlideBlock({
              id: this.id,
              type: ContentType[this.type],
              field: this.uploadFieldName,
              file,
            })
            .subscribe(
              (_response: ApiSlideSettings) => {
                this.getSettings();
                this.uploading = false;
              },
              () => {
                this.uploading = false;
                this.snackbarService.error(`error.${ArsultimaErrorTypes.FILE_SIZE_TOO_BIG}`);
              },
            );
        }
      });
    } else {
      const formData = this.form.getRawValue();
      this.contentService.saveBlock(formData).subscribe((response: BlockSettings) => {
        this.mapSettingsToForm(new ApiBlockSettings(response));
        this.id = response.ID;
        this.upload(files);
      });
    }
  }

  deleteImage(): void {
    this.form.patchValue({ nom_pic: null });
    this.imageUrl = '';
  }

  delete(): void {
    const dialogRef = this.dialog.open(DeleteModalComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.contentService.deleteBlock(parseInt(this.id, 10)).subscribe(() => {
          this.snackbarService.success('messages.blockRemoved');
          this.goBack();
        });
      }
    });
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();

      if (this.numberOfRotation) {
        this.contentService
          .rotateBlock({
            id: this.id,
            numberOfRotation: this.numberOfRotation,
          })
          .pipe(
            catchError(error => {
              console.error('Rotation failed:', error);
              return of(null);
            }),
            switchMap(() => this.contentService.saveBlock(formValue)),
          )
          .subscribe(
            () => {
              this.goBack();
            },
            error => {
              console.error('Error saving block:', error);
            },
          );
      } else {
        this.contentService.saveBlock(formValue).subscribe(
          () => {
            this.goBack();
          },
          error => {
            console.error('Error saving block:', error);
          },
        );
      }
    }
  }

  goBack(): void {
    this.dismiss.emit();
    this.navigateService.navigateBackUrl();
  }

  private getSettings(): void {
    if (this.page && this.type && this.id) {
      this.contentService.getSettings().subscribe(settings => {
        const data = this.getFormData(settings, this.page, this.type, this.id);
        this.mapSettingsToForm(data);

        if (data.nom_pic) {
          this.imageUrl = this.imageService.getImageUrl(this.id, {
            prefix: IMAGES_FILENAME_PREFIX.blocks,
            timestamp: true,
          });
        }
      });
    } else {
      this.disableControls();
    }
  }

  private getFormData(
    settings: ApiSettings,
    page: string,
    type: string,
    id: string,
  ): ApiBlockSettings {
    if (settings[page] && settings[page][type]) {
      const data = settings[page][type].find(data => data.ID === id);

      return new ApiBlockSettings(data);
    }
    return null;
  }

  private mapSettingsToForm(data: ApiBlockSettings): void {
    const props = Object.keys(data);
    const disableProps = ['ID', 'date_maj', 'page'];

    props.forEach(prop => {
      this.form.patchValue({
        [prop]: data[prop],
      });

      if (this.editMode && disableProps.includes(prop)) {
        this.form.controls[prop].disable();
      }
    });
  }

  private disableControls(): void {
    const disableProps = ['ID', 'date_maj', 'page'];
    disableProps.forEach(prop => this.form.controls[prop].disable());
  }

  get shouldDisableUpload(): boolean {
    const formId = this.form.get('ID')?.value;
    return formId !== '0' && !this.form.valid;
  }
}
